import { Instructor, FilterOption, EventCategory, Country } from "@features/events/types";

export class InstructorFilterOptionsBuilder {
  private instructors: Instructor[];
  private allCountries: Country[];
  private allLanguages: Array<{ englishLabel: string; iso: string; svg: string }>;
  private allCategories: EventCategory[];

  constructor(
    instructors: Instructor[],
    allCountries: Country[],
    allLanguages: Array<{ englishLabel: string; iso: string; svg: string }>,
    allCategories: EventCategory[]
  ) {
    this.instructors = instructors;
    this.allCountries = allCountries;
    this.allLanguages = allLanguages;
    this.allCategories = allCategories;
  }

  /**
   * Get country options based on instructors that actually have those countries
   */
  getCountryOptions(): FilterOption[] {
    // Extract unique country ISO3 codes from instructors
    const uniqueCountryISO3s = new Set<string>();
    
    this.instructors.forEach((instructor) => {
      if (instructor.geoAddress?.countryISO3) {
        uniqueCountryISO3s.add(instructor.geoAddress.countryISO3);
      }
    });

    // Map to country choices and filter to only include countries present in instructors
    return this.allCountries
      .filter((country) => uniqueCountryISO3s.has(country.iso3))
      .map((country) => ({
        label: country.name,
        value: country.iso3,
        flag: country.svg,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Get language options based on instructors that actually speak those languages
   */
  getLanguageOptions(): FilterOption[] {
    // Extract unique language codes from instructors
    const uniqueLanguageCodes = new Set<string>();
    
    this.instructors.forEach((instructor) => {
      if (instructor.languageCodes && Array.isArray(instructor.languageCodes)) {
        instructor.languageCodes.forEach((code) => {
          uniqueLanguageCodes.add(code);
        });
      }
    });

    // Map to language choices and filter to only include languages present in instructors
    return this.allLanguages
      .filter((language) => uniqueLanguageCodes.has(language.iso))
      .map((language) => ({
        label: language.englishLabel,
        value: language.iso,
        flag: language.svg,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Get specialization options based on instructors that actually have those specializations
   */
  getSpecializationOptions(): FilterOption[] {
    // Extract unique category IDs from instructors
    const uniqueCategoryIds = new Set<string>();
    
    this.instructors.forEach((instructor) => {
      if (instructor.categoryIds && Array.isArray(instructor.categoryIds)) {
        instructor.categoryIds.forEach((categoryId: string) => {
          uniqueCategoryIds.add(categoryId);
        });
      }
    });

    // Map to specialization choices and filter to only include categories present in instructors
    return this.allCategories
      .filter((category) => uniqueCategoryIds.has(category.id))
      .map((category) => ({
        label: category.name,
        value: category.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Get all filter options at once
   */
  getAllOptions(): {
    countries: FilterOption[];
    languages: FilterOption[];
    specializations: FilterOption[];
  } {
    return {
      countries: this.getCountryOptions(),
      languages: this.getLanguageOptions(),
      specializations: this.getSpecializationOptions(),
    };
  }
}

